import {Box, Skeleton} from '@mantine/core';
import {useState} from 'react';
import {COMPANIES_CRUMB, HOME_CRUMB} from '../../../config/constants/crumb';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {COMPANIES, HOME} from '../../../config/constants/routes';
import {FOLLOW_COMPANY_RANKING_TERMS} from '../../../core/entities/company/constants';
import type {FollowRankingListTermType} from '../../../core/entities/company/schema';
import {
  type ResultGetWorkAttractiveConditions,
  useGetFollowCompanyRankingList,
} from '../../../core/repositories/companyRepository';
import {useInfiniteCompanyList} from '../../../core/repositories/companyRepository';
import type {IndustryCategoriesReturnValueType} from '../../../core/repositories/reportsRepository';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {sortWorkAttractiveConditions} from '../usecase/sortWorkAttractiveConditions';
import {useHandleCompanyForSearch} from '../usecase/useHandleCompanyForSearch';
import {useSyncAttractiveConditionWithQuery} from '../usecase/useSyncAttractiveConditionWithQuery';
import {CompanyListHeader} from './Container/CompanyListHeader';
import {CompanyCard} from './Container/Presenter/CompanyCard';
import {CompanyFilter} from './Container/Presenter/CompanyFilter';
import {FollowCompanyRankingFrame} from './Container/Presenter/FollowCompanyRankingFrame';

type Props = {
  pageTitle: string;
  industryCategoryId: number;
  industryIds: number[];
  industryCategories?: IndustryCategoriesReturnValueType;
  attractiveConditionsData?: ResultGetWorkAttractiveConditions;
  attractiveConditionQuery?: string[];
};

export const CompanyListIndustryPage: React.FC<Props> = ({
  pageTitle,
  industryCategoryId,
  industryIds,
  industryCategories,
  attractiveConditionsData,
  attractiveConditionQuery = [],
}: Props) => {
  const [filterAttractiveConditions, setFilterAttractiveConditions] =
    useSyncAttractiveConditionWithQuery(attractiveConditionQuery);
  const {
    searchWord,
    handleSearchInputChange,
    handleFilterEmployeeCountChange,
    filterMinEmployeeCount,
    filterMaxEmployeeCount,
    attractiveConditionsList,
  } = useHandleCompanyForSearch(
    industryCategories,
    attractiveConditionsData,
    filterAttractiveConditions,
  );

  const {data, isLoading: isLoadingCompanies} = useInfiniteCompanyList({
    queryParams: {
      industryIDs: industryIds,
      industryCategoryIDs: industryCategoryId > 0 ? [industryCategoryId] : [],
      limit: 100,
      page: 1,
      order: 'pageType',
      cacheID: '',
      minEmployeeCount: filterMinEmployeeCount,
      maxEmployeeCount: filterMaxEmployeeCount,
      workAttractiveConditionIDs: filterAttractiveConditions.map((item) =>
        Number(item),
      ),
    },
  });

  const industryCompanies =
    data?.pages
      .flatMap((page) => page?.companies ?? [])
      .map((company) => ({
        ...company,
        // 絞り込み条件に合致するものを先頭に持ってくる
        workAttractiveConditions: sortWorkAttractiveConditions(
          company.workAttractiveConditions,
          filterAttractiveConditions,
        ),
      })) ?? [];
  const companiesForSearch = industryCompanies.map((c) => {
    return {
      id: c.id,
      name: c.name,
    };
  });

  const [followRankingListTerm, setFollowRankingListTerm] =
    useState<FollowRankingListTermType>(FOLLOW_COMPANY_RANKING_TERMS.weekly);
  const [isLastYearRanking, setIsLastYearRanking] = useState(false);

  const {data: followRankingList, isLoading: isLoadingFollowRankingList} =
    useGetFollowCompanyRankingList({
      limit: 10,
      industryIDs: industryIds.length > 0 ? industryIds : undefined,
      industryCategoryID:
        industryIds.length === 0 ? industryCategoryId : undefined,
      term:
        followRankingListTerm === FOLLOW_COMPANY_RANKING_TERMS.all
          ? undefined
          : followRankingListTerm,
      isLastYear: isLastYearRanking,
    });

  const isDisPlayRankingList =
    (isLoadingFollowRankingList ||
      (followRankingList?.ok && followRankingList.value.ranking.length > 0)) ??
    false;

  const crumbs = [
    {label: HOME_CRUMB, href: HOME},
    {label: COMPANIES_CRUMB, href: COMPANIES},
    {
      label: pageTitle,
      href: `${COMPANIES}/industry/${industryCategoryId}${industryIds.length > 0 ? `?industryIDs=${industryIds.join(',')}` : ''}`,
    },
  ];

  const {isPC} = useMediaQuery();
  const width =
    typeof window === 'undefined' || isPC
      ? 282
      : (window?.screen.width ?? 282) - 32;
  const companiesLength = industryCompanies.length;
  return (
    <>
      <Box className="hidden pc:block">
        <GoogleAdBanner
          googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
        />
      </Box>
      <Box className="w-full mx-auto flex pc:w-[1200px] justify-between">
        <Box className="w-full pc:w-[1200px]">
          <CompanyListHeader pageTitle={pageTitle} crumbs={crumbs} />
          <Box className="bg-white mx-16 rounded-16 shadow-card pc:bg-transparent pc:mx-0 pc:rounded-none pc:shadow-transparent">
            <CompanyFilter
              companies={companiesForSearch}
              searchWord={searchWord}
              handleSearchInputChange={handleSearchInputChange}
              handleFilterEmployeeCountChange={handleFilterEmployeeCountChange}
              attractiveConditionsList={attractiveConditionsList}
              handleFilterAttractiveConditionChange={
                setFilterAttractiveConditions
              }
              filterAttractiveConditions={filterAttractiveConditions}
            />
          </Box>
        </Box>
      </Box>
      <div className="w-full pc:w-[1200px] pc:mx-auto">
        {isDisPlayRankingList && (
          <FollowCompanyRankingFrame
            ranking={followRankingList}
            isLoadingRanking={isLoadingFollowRankingList}
            rankingTerm={followRankingListTerm}
            setRankingTerm={setFollowRankingListTerm}
            isLastYearRanking={isLastYearRanking}
            setIsLastYearRanking={setIsLastYearRanking}
            industryIds={industryIds}
            industryCategoryId={industryCategoryId}
          />
        )}
      </div>
      <div className="bg-white w-full">
        <div className="pc:w-[1200px] pc:mx-auto grid grid-cols-1 pc:grid-cols-4 mt-12 p-16 pc:px-0 gap-16 pc:gap-24">
          {industryCompanies.map((company) => (
            <CompanyCard
              key={`card-${company.id}`}
              company={company}
              width={width}
              bgColorStyle={'bg-white pc:bg-transparent'}
            />
          ))}
          {isLoadingCompanies &&
            Array.from({length: 20}).map((_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: order won't be changed
              <div key={index}>
                <Skeleton height={150} width={282} />
                <Skeleton height={30} width={282} />
                <Skeleton height={30} width={282} />
              </div>
            ))}
        </div>
        {companiesLength === 0 && !isLoadingCompanies && (
          <div className="flex w-full bg-white py-16 px-16 pc:px-0">
            <div className="w-full flex justify-center items-center">
              <p className="text-black-2">企業が見つかりませんでした。</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
